import type { ICheckEnrolmentCmd } from "@/data/commands/support/CheckEnrolmentCmd";
import type { ICreateIssueCmd } from "@/data/commands/support/CreateIssueCmd";
import type { ISubmitSaleInquiryCmd } from "@/data/commands/support/SubmitSaleInquiryCmd";
import type { IUpdateIssueCmd } from "@/data/commands/support/UpdateIssueCmd";
import type { IUpdateSaleInquiryCmd } from "@/data/commands/support/UpdateSaleInquiryCmd";
import type { IEnrolmentCheckDto, IIssue, ISaleInquiry } from "@/data/model/Support";
import type { IIssuesQry } from "@/data/queries/support/IssuesQry";
import { DateTime } from "@/data/util/DateTime";

import {
    BaseServiceDao,
    type IEditableDataSource,
    type IPaginatedResult,
    parseCollection,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class SupportDao extends BaseServiceDao {
    public static className = "SupportDao";

    constructor() {
        super("support");
    }

    public async getIssues(qry: IIssuesQry): Promise<IPaginatedResult<IIssue>> {
        return this.get("issue/", qry, (f) => parsePaginatedResult(f, SupportDao.parseIssue));
    }

    public async createIssue(cmd: ICreateIssueCmd): Promise<IIssue> {
        return this.post("issue/", cmd, SupportDao.parseIssue);
    }

    public async updateIssue(id: string, cmd: IUpdateIssueCmd): Promise<IIssue> {
        return this.put("issue/" + id, cmd, SupportDao.parseIssue);
    }

    public async removeIssue(id: string): Promise<void> {
        return this.delete("issue/" + id);
    }

    public async getInquiries(qry: IIssuesQry): Promise<IPaginatedResult<ISaleInquiry>> {
        return this.get("inquiry/", qry, (f) => parsePaginatedResult(f, SupportDao.parseSaleInquiry));
    }

    public async submitInquiry(cmd: ISubmitSaleInquiryCmd): Promise<void> {
        return this.post("inquiry/", cmd);
    }

    public async updateInquiry(id: string, cmd: IUpdateSaleInquiryCmd): Promise<IIssue> {
        return this.put("inquiry/" + id, cmd, SupportDao.parseIssue);
    }

    public async removeInquiry(id: string): Promise<void> {
        return this.delete("inquiry/" + id);
    }

    public async checkEnrolment(cmd: ICheckEnrolmentCmd): Promise<IEnrolmentCheckDto> {
        return this.post("inquiry/check", cmd, (f) => {
            return {
                accountExists: f.accountExists,
                enrolmentComplete: f.enrolmentComplete,
                missingFields: parseCollection(f.missingFields, (c) => c),
            };
        });
    }

    public issueDataSource(): IEditableDataSource<IIssue> {
        return {
            getData: (a) => this.getIssues(a),
            create: (a) => this.createIssue(a),
            update: (a, b) => this.updateIssue(a, b),
            remove: (a) => this.removeIssue(a),
            getSingle: undefined,
        } as any;
    }

    public saleInquiryDataSource(): IEditableDataSource<ISaleInquiry> {
        return {
            getData: (a) => this.getInquiries(a),
            create: (a) => this.submitInquiry(a),
            update: (a, b) => this.updateInquiry(a, b),
            remove: (a) => this.removeInquiry(a),
            getSingle: undefined,
        } as any;
    }

    public static parseIssue(literal: any): IIssue {
        return {
            id: literal.id,
            state: parseInt(literal.state),
            received: DateTime.parseIsoDate(literal.received),
            message: literal.message,
            firstName: literal.firstName,
            lastName: literal.lastName,
            email: literal.email,
            phoneNumber: literal.phoneNumber,
            userId: literal.userId == null ? undefined : literal.userId,
            lastModified: literal.lastModified == null ? undefined : DateTime.parseIsoDate(literal.lastModified),
        };
    }

    public static parseSaleInquiry(literal: any): ISaleInquiry {
        return {
            id: literal.id,
            state: parseInt(literal.state),
            language: parseInt(literal.language),
            pricingModel: parseInt(literal.pricingModel),
            received: DateTime.parseIsoDate(literal.received),
            company: literal.company,
            location: literal.location,
            website: literal.website == null ? undefined : literal.website,
            firstName: literal.firstName,
            lastName: literal.lastName,
            email: literal.email,
            phoneNumber: literal.phoneNumber,
            userId: literal.userId == null ? undefined : literal.userId,
            lastModified: literal.lastModified == null ? undefined : DateTime.parseIsoDate(literal.lastModified),
        };
    }
}
